import { useMemo, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';

import { usePathname } from 'src/routes/hooks';

import { useResponsive } from 'src/hooks/use-responsive';

import Logo from 'src/components/logo';
import Scrollbar from 'src/components/scrollbar';
import { NavSectionVertical } from 'src/components/nav-section';
import { NavItemBaseProps } from 'src/components/nav-section/types';

import { NAV } from '../config-layout';
import { useNavData } from './config-navigation';
import NavToggleButton from '../common/nav-toggle-button';

// ----------------------------------------------------------------------

type NavGroup = {
  subheader: string;
  items: NavItemBaseProps[];
};

const isVisibleForRole = (roles: string[] | undefined, currentRole: string) =>
  !roles || roles.includes(currentRole);

const filterItems = (items: NavItemBaseProps[], currentRole: string): NavItemBaseProps[] =>
  items
    .filter((item) => isVisibleForRole(item.roles, currentRole))
    .map((item) =>
      item.children
        ? { ...item, children: filterItems(item.children, currentRole) }
        : item
    )
    .filter((item) => !item.children || item.children.length > 0);

const filterNavData = (data: NavGroup[], currentRole: string): NavGroup[] =>
  data
    .map((group) => ({ ...group, items: filterItems(group.items, currentRole) }))
    .filter((group) => group.items.length > 0);

// ----------------------------------------------------------------------

type Props = {
  openNav: boolean;
  onCloseNav: VoidFunction;
};

const roleClaimName = 'https://miami-ship/roles';

export default function NavVertical({ openNav, onCloseNav }: Props) {
  const { user } = useAuth0();
  const roles = user?.[roleClaimName]?.split(' ');
  const currentRole = roles && roles.length ? roles[0] : '';
  const pathname = usePathname();

  const lgUp = useResponsive('up', 'lg');

  const navData = useNavData();

  const filteredNavData = useMemo(
    () => filterNavData(navData as NavGroup[], currentRole),
    [navData, currentRole]
  );

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Logo sx={{ mt: 3, ml: 4, mb: 1 }} />

      <NavSectionVertical
        data={filteredNavData}
        slotProps={{
          currentRole,
        }}
      />

      <Box sx={{ flexGrow: 1 }} />

      {/* <NavUpgrade /> */}
    </Scrollbar>
  );

  return (
    <Box
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_VERTICAL },
      }}
    >
      <NavToggleButton />

      {lgUp ? (
        <Stack
          sx={{
            height: 1,
            position: 'fixed',
            width: NAV.W_VERTICAL,
            borderRight: (theme) => `dashed 1px ${theme.palette.divider}`,
          }}
        >
          {renderContent}
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: {
              width: NAV.W_VERTICAL,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
